// AGC: Voice Agent v2 confirmation UI. Replaces v1's TTS/continuous-mic flow with a card stack.

import React, { useState } from 'react'
import { CheckCircle, AlertCircle, Mic, Pencil, X, Send } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import type { VoiceAgentV2Response, ToolCallCard } from '../../services/voiceAgentV2'

interface VoiceAgentConfirmProps {
  response: VoiceAgentV2Response
  onConfirm: () => void
  onReject: () => void
  onEdit: () => void
  onSendText: (text: string) => void
  onClose: () => void
  busy?: boolean
}

function ToolCallCardView({ card }: { card: ToolCallCard }) {
  const isError = !!card.error || card.result?.success === false
  return (
    <div className={`rounded-lg border p-3 flex items-start gap-2 ${
      isError ? 'border-red-400/30 bg-red-500/10' : 'border-emerald-400/30 bg-emerald-500/10'
    }`}>
      {isError ? (
        <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
      ) : (
        <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-white text-sm font-medium">{card.name}</div>
        {card.error && <div className="text-red-200 text-xs mt-1">{card.error}</div>}
        {card.result && !card.error && (
          <div className="text-white/70 text-xs mt-1 font-mono break-all">
            {JSON.stringify(card.result).slice(0, 200)}
          </div>
        )}
      </div>
    </div>
  )
}

export const VoiceAgentConfirm: React.FC<VoiceAgentConfirmProps> = ({
  response, onConfirm, onReject, onEdit, onSendText, onClose, busy = false,
}) => {
  const { t } = useTranslation()
  const [textMode, setTextMode] = useState(false)
  const [textValue, setTextValue] = useState('')
  const hasPending = !!response.pendingConfirmation
  // AGC: v3.9.42 — Strip WhatsApp-specific reply prompts. Backend prompt is shared with WhatsApp,
  // AGC: so we scrub client-side without touching the agent. WhatsApp is unaffected.
  const displayReply = (response.replyText || '')
    .replace(/Responda\s*\*?\*?Sim\*?\*?\s*ou\s*\*?\*?1\*?\*?\s*para\s*confirmar[.!]?/gi, '')
    .replace(/Responda\s*\*?\*?No\*?\*?\s*ou\s*\*?\*?2\*?\*?[^\n]*/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return (
    <div className="max-w-md w-full mx-auto flex flex-col gap-3">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-white/60 hover:text-white/90 rounded-lg hover:bg-white/10"
          aria-label={t('voice.agent.v2Close')}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {response.transcription && (
        <div className="rounded-lg bg-blue-500/10 border border-blue-400/30 p-3">
          <div className="text-blue-200 text-xs uppercase tracking-wide mb-1">{t('voice.agent.v2Heard')}</div>
          <div className="text-white text-sm italic">&ldquo;{response.transcription}&rdquo;</div>
        </div>
      )}
      <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-4">
        <div className="text-emerald-200 text-xs uppercase tracking-wide mb-1">{t('voice.agent.v2Agent')}</div>
        <div className="text-white text-base leading-relaxed whitespace-pre-wrap">
          {displayReply}
        </div>
      </div>
      {response.toolCallCards.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-white/60 text-xs uppercase tracking-wide">{t('voice.agent.v2Actions')}</div>
          {response.toolCallCards.map((card, i) => <ToolCallCardView key={i} card={card} />)}
        </div>
      )}
      {textMode && !response.entryCreated && (
        <div className="flex flex-col gap-2 mt-2">
          <textarea
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            placeholder={t('voice.agent.v2CorrectionPlaceholder')}
            rows={3}
            className="w-full rounded-lg bg-white/10 border border-white/20 p-3 text-white placeholder-white/40 text-sm focus:outline-none focus:border-emerald-400"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setTextMode(false); setTextValue('') }}
              disabled={busy}
              className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium disabled:opacity-50"
            >{t('voice.agent.v2Cancel')}</button>
            <button
              type="button"
              onClick={() => {
                if (textValue.trim()) {
                  onSendText(textValue.trim())
                  setTextValue('')
                  setTextMode(false)
                }
              }}
              disabled={busy || !textValue.trim()}
              className="flex-1 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
            ><Send className="w-4 h-4" />{t('voice.agent.v2Send')}</button>
          </div>
        </div>
      )}
      {!textMode && (
        response.entryCreated ? (
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="flex-1 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-50"
            >{t('voice.agent.v2OK')}</button>
          </div>
        ) : (
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={onEdit}
              disabled={busy}
              className="flex-1 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-1"
            ><Mic className="w-4 h-4" />{t('voice.agent.v2Edit')}</button>
            <button
              type="button"
              onClick={() => setTextMode(true)}
              disabled={busy}
              className="px-4 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium disabled:opacity-50 flex items-center justify-center"
              aria-label={t('voice.agent.v2TypeCorrection')}
              title={t('voice.agent.v2TypeCorrection')}
            ><Pencil className="w-4 h-4" /></button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="flex-1 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-50"
            >{t('voice.agent.v2Confirm')}</button>
          </div>
        )
      )}
    </div>
  )
}

export default VoiceAgentConfirm
